'use client';

import { debounce } from '@smartproxy-web/shared/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import { BlockReferenceArray } from '@/components/cms/types/block';
import { BlockComponentModel } from '@/components/cms/types/blok-component';
import { SECTION_NAMES } from '@/components/integrations/trackingScripts/constants';
import { ScrapingTemplateCardProps } from '@/components/organisms/ScrapingTemplateLibrary/Components/ScrapingTemplateCard';

import ScrapingTemplateCard from '@/decodo/blocks/ScrapingTemplateLibrary/Components/ScrapingTemplateCard';
import {
  getTagsForCards,
  useScrapingTemplateLibraryGridContainerHeight,
} from '@/decodo/blocks/ScrapingTemplateLibrary/utils';
import { Input } from '@/decodo/components/atoms/Input';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/decodo/components/atoms/RichTextField/RichTextFieldStrapi';
import { BlockWrapper } from '@/features/utils/BlockWrapperDC';
import { getWrapperMaxWidth } from '@/features/utils/utils';

import NoTemplatesFound from './Components/NoTemplatesFound';
import { TagsFilter } from './Components/TagsFilter';

export type ScrapingTemplateLibraryProps = {
  title?: RichTextFieldStrapiProps;
  description?: RichTextFieldStrapiProps;
  scrapingTemplateCards?: BlockReferenceArray<ScrapingTemplateCardProps>;
} & {
  theme: BlockComponentModel<any>['theme'];
};

const ScrapingTemplateLibrary = (props: ScrapingTemplateLibraryProps) => {
  const { texts } = useTranslation();

  const [search, setSearch] = useState('');
  const [selectedTag, setSelectedTag] = useState('');

  const cards = props.scrapingTemplateCards?.data || [];
  const allTags = getTagsForCards(cards);

  const handleSearch = debounce((e: any) => {
    setSearch(e.target.value);
  }, 300);

  function getCardsForTag(cardsParam: typeof cards, tagParam: string) {
    return cardsParam.filter((card) => {
      const associatedTags = card.attributes.scraping_template_tags.data;

      if (!associatedTags?.length) return false;

      return !!associatedTags.find((associatedTag) => {
        return associatedTag.attributes.title === tagParam;
      });
    });
  }

  function getCardsForSearch(cardsParam: typeof cards, search: string) {
    return cardsParam.filter((card) => {
      const splitCardTitle = card.attributes.title.split(' ');
      const splitSearch = search.split(' ');

      const allWordsPresent = splitSearch.every((word) =>
        splitCardTitle.some((titleWord) =>
          titleWord.toLowerCase().includes(word.toLowerCase())
        )
      );

      return allWordsPresent;
    });
  }

  const cardsToDisplay = (() => {
    if (selectedTag && search) {
      const cardsWithTag = getCardsForTag(cards, selectedTag);
      const cardsWithTagAndSearch = getCardsForSearch(cardsWithTag, search);

      return cardsWithTagAndSearch;
    }
    if (selectedTag) {
      return getCardsForTag(cards, selectedTag);
    }
    if (search) {
      return getCardsForSearch(cards, search);
    }

    return cards;
  })();

  const { cardContainerRef, maxHeight } =
    useScrapingTemplateLibraryGridContainerHeight({
      nbCards: cardsToDisplay.length,
    });

  return (
    <BlockWrapper
      data-tracker-section={SECTION_NAMES.SCRAPING_TEMPLATE_LIBRARY}
      contentFit='mid'
      classNames={{ root: 'relative' }}
    >
      <div className='mx-auto max-w-[1360px]'>
        <div
          className={cn(
            'mx-auto max-w-[1024px] md:text-center',
            'pb-8 md:pb-20'
          )}
        >
          {props.title && (
            <div className='mb-5 text-gray-950 md:mb-6'>
              <RichTextFieldStrapi block={props.title} />
            </div>
          )}
          {props.description && (
            <div className='text-body-l font-mono text-gray-500'>
              <RichTextFieldStrapi block={props.description} />
            </div>
          )}
        </div>

        <div
          className={cn(
            'mx-auto mb-8 flex flex-col items-center justify-between md:mb-20 md:flex-row',
            getWrapperMaxWidth('mid')
          )}
        >
          <TagsFilter
            tags={allTags}
            selectedTag={selectedTag}
            handleSelectTag={setSelectedTag}
          />
          <div className='mt-3 w-full pr-8 md:mt-0 md:w-1/3 md:pr-0'>
            <Input
              data-tracker-component='search'
              isSearch
              classNames={{
                input: {
                  inputElement: 'dark:bg-transparent',
                },
                rightIcon: 'text-base-black',
                search: 'bg-transparent',
              }}
              name='search'
              onChange={handleSearch}
              placeholder={texts.feature_library.input_search_placeholder}
            />
          </div>
        </div>

        {!cardsToDisplay.length ? (
          <AnimatePresence mode='wait'>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { duration: 0.2 } }}
              exit={{ opacity: 0, transition: { duration: 0.2 } }}
              className='mt-20'
            >
              <NoTemplatesFound />
            </motion.div>
          </AnimatePresence>
        ) : (
          <div
            className={cn(
              'absolute -ml-4 overflow-y-auto overflow-x-hidden',
              // leave additional padding to the side on mobile to allow scrolling past the library component
              'mr-8 pl-4 pr-6 md:mr-0'
            )}
            style={{
              // ensures scrollbars match the theme on safari
              colorScheme: props.theme,
              height: maxHeight,
            }}
          >
            <div
              ref={cardContainerRef}
              className={cn(
                getWrapperMaxWidth('mid'),
                'w-full',
                'mx-auto',
                'pb-8',
                'grid gap-6',
                'grid-cols-1',
                'md:grid-cols-2',
                'desktop:grid-cols-3'
              )}
            >
              {cardsToDisplay.map((card) => (
                <AnimatePresence mode='popLayout' key={card.id}>
                  <motion.div
                    initial={{ x: -5, opacity: 0 }}
                    animate={{
                      x: 0,
                      opacity: 1,
                      transition: { duration: 0.2 },
                    }}
                    exit={{ x: -5, opacity: 0, transition: { duration: 0.05 } }}
                    className={cn('flex h-full flex-col')}
                  >
                    <ScrapingTemplateCard {...card.attributes} />
                  </motion.div>
                </AnimatePresence>
              ))}

              {/* Preserve grid layout when only one item is available, while retaining stretchiness  */}
              {cardsToDisplay.length <= 2 && <div />}
              {cardsToDisplay.length === 1 && <div />}
            </div>
          </div>
        )}
        {/* compensate for the position: absolute section */}
        <div style={{ height: maxHeight }} />
      </div>
    </BlockWrapper>
  );
};

export default ScrapingTemplateLibrary;
